import moment from 'moment/moment';
import { DateFormats } from 'constants/common';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { DealActivityType, DealActivityLinkType, CalendarEventViewModel } from '@trnsact/trnsact-shared-types';

import { CalendarView } from '../types';
import { EventListingRow } from '../ui/table/types';
import { CalendarEvent, CalendarDateRange } from '../ui/calendar';
import { CalendarEventListingEntity, ReDateEventsInput } from '../api';
import { CreateEventFormValues } from '../ui/dialogs/createEventFormSchema';
import { UpdateCalendarViewFormValues } from '../ui/dialogs/updateCalendarViewFormSchema';
import { CalendarEventUpdateFormValues } from '../ui/card/eventUpdateForm';
import { CreateCallActivityVariables, UpdateCallActivityVariables } from '../../api/types';

interface EventDatesFormValues {
  allDay: boolean;
  endDate: string | null;
  startDate: string | null;
  endTime?: string | null;
  startTime?: string | null;
}

interface EventDatesPayload {
  allDay: boolean;
  endAt?: string;
  endDate?: string;
  startAt?: string;
  startDate?: string;
}

export class CalendarMapper {
  static mapFormDtoToCreate = (
    values: CreateEventFormValues,
    currentUserAccountId: string
  ): CreateCallActivityVariables['input'] => {
    if (!currentUserAccountId) throw new Error('User accountId is required to create a deal activity');

    const {
      allDay,
      endDate,
      endTime,
      startTime,
      startDate,
      contactId,
      accountId,
      assignedTo,
      activityStatus,
      ...rest
    } = values;

    return {
      type: DealActivityType.Event,
      parentId: currentUserAccountId,
      parentType: DealActivityLinkType.Account,
      activityStatus,
      event: {
        accountId,
        ...rest,
        ...(contactId ? { contactId } : {}),
        ...CalendarMapper.buildEventDatesPayload({ allDay, startDate, endDate, startTime, endTime }),
      },
    };
  };

  static mapFormDtoToUpdate = (
    event: CalendarEventViewModel,
    values: CalendarEventUpdateFormValues
  ): UpdateCallActivityVariables['input'] => {
    const { allDay, startDate, endDate, startTime, endTime, ...rest } = values;

    return {
      id: event.id,
      parentId: event.parentId,
      type: DealActivityType.Event,
      parentType: DealActivityLinkType.Account,
      event: {
        ...rest,
        ...CalendarMapper.buildEventDatesPayload({ allDay, startDate, endDate, startTime, endTime }),
      },
    };
  };

  static mapFormDtoToUpdateView = (id: string, values: UpdateCalendarViewFormValues) => ({
    id,
    name: values.name,
    color: values.color,
  });

  static mapDtoToReDate = (dto: EventInteractionArgs<CalendarEvent>): ReDateEventsInput['input']['events'][number] => {
    if (dto.event.allDay) {
      return {
        allDay: true,
        id: dto.event.id,
        startDate: moment(dto.start).format(DateFormats.IsoDate),
        endDate: moment(dto.end).subtract(1, 'day').format(DateFormats.IsoDate),
      };
    }

    return {
      allDay: false,
      id: dto.event.id,
      endAt: moment(dto.end).toISOString(),
      startAt: moment(dto.start).toISOString(),
    };
  };

  static getVisibleRange = (view: CalendarView, date: Date): CalendarDateRange => {
    const current = moment(date);

    switch (view) {
      case CalendarView.Day:
        return {
          from: current.clone().startOf('day').toISOString(),
          to: current.clone().endOf('day').toISOString(),
        };
      case CalendarView.Week:
        return {
          from: current.clone().startOf('week').toISOString(),
          to: current.clone().endOf('week').toISOString(),
        };
      case CalendarView.Month:
      default:
        return {
          from: current.clone().startOf('month').startOf('week').toISOString(),
          to: current.clone().endOf('month').endOf('week').toISOString(),
        };
    }
  };

  /**
   * Day/Week render a time grid where events that overlap in time are laid out side by side, so
   * dozens of activities logged at the same instant each get a sliver of the column width and
   * effectively disappear. Logged activities (calls, notes, emails) are read-only history rather
   * than scheduled time blocks, so they move to the all-day band, which collapses overflow into a
   * "+N more" popover. Real calendar events keep their place in the time grid. `start` is untouched,
   * so the card can still show the activity's time.
   */
  static moveActivitiesToAllDayBand = (events: CalendarEvent[] = []): CalendarEvent[] =>
    events.map(event => (event.activity ? { ...event, allDay: true } : event));

  static getEventsSignature = (events: CalendarEvent[]): string =>
    events
      .map(event => {
        const start = event.start ? new Date(event.start).getTime() : 0;
        const end = event.end ? new Date(event.end).getTime() : 0;

        return `${event.id}:${start}:${end}:${event.allDay ? 1 : 0}:${event.sharing?.color ?? ''}`;
      })
      .join('|');

  static moveEventInList = (events: CalendarEvent[], dto: EventInteractionArgs<CalendarEvent>): CalendarEvent[] => {
    const start = new Date(dto.start);
    const end = new Date(dto.end);

    return events.map(event =>
      event.id === dto.event.id ? { ...event, start, end, durationMinutes: moment(end).diff(start, 'minutes') } : event
    );
  };

  static combineDateAndTime = (date: string, time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return moment(date).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 }).toISOString();
  };

  static addOneHour = (time: string) => {
    const result = moment(time, 'HH:mm').add(1, 'hour');
    return { time: result.format('HH:mm'), nextDay: result.date() !== moment(time, 'HH:mm').date() };
  };

  private static buildEventDatesPayload = ({
    allDay,
    endDate,
    startDate,
    endTime,
    startTime,
  }: EventDatesFormValues): EventDatesPayload => {
    const payload: EventDatesPayload = { allDay };

    if (allDay) {
      if (startDate) payload.startDate = moment(startDate).format(DateFormats.IsoDate);
      if (endDate) payload.endDate = moment(endDate).format(DateFormats.IsoDate);
    } else {
      if (startDate && startTime) payload.startAt = CalendarMapper.combineDateAndTime(startDate, startTime);
      if (endDate && endTime) payload.endAt = CalendarMapper.combineDateAndTime(endDate, endTime);
    }

    return payload;
  };

  public static toEventListingRow = (activity: CalendarEventListingEntity): EventListingRow => ({
    id: activity.id,
    type: activity.eventType,
    allDay: activity.allDay,
    subject: activity.subject,
    due: activity.due ?? null,
    priority: activity.priority,
    status: activity.activityStatus,
    account: activity.account.name ?? '',
    description: activity.description ?? null,
  });
}
